<template>
  <DefaultCrudView
    @create-new="newDeliverable"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="deliverables"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.active`]="{ item }">
          <v-chip small :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? $t('yes') : $t('no') }}
          </v-chip>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.default_format_desc`]="{ item }">
          <span v-if="item.default_format_desc">{{ item.default_format_desc }}</span>
          <span v-else>—</span>
        </template>

        <template v-slot:[`item.icon`]="{ item }">
          <v-icon v-if="item.icon">{{ item.icon }}</v-icon>
          <span v-else>—</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editDeliverable(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item v-if="item.active" @click="toggleActive(item, false)">
                <v-list-item-icon>
                  <v-icon medium>mdi-package-variant-remove</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('deactivate_deliverable') }}</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.active" @click="toggleActive(item, true)">
                <v-list-item-icon>
                  <v-icon medium>mdi-package-variant-closed-check</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('activate_deliverable') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item @click="openDeliverableBillableModal(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-cash-multiple</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_billables') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <DeliverableBillableModal ref="deliverableBillableModal"></DeliverableBillableModal>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import DeliverableBillableModal from '@/components/business/DeliverableBillableModal.vue'

import AppActions from '@/store/app/actions-types'

import DeliverableAPI from '@/services/api/business/deliverable'

export default {
  name: 'CustomerDeliverables',

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    DeliverableBillableModal,
  },

  computed: {
    customerId() {
      return this.$route.params.customerId
    },

    customerName() {
      return this.$route.query.customerName || this.customerId
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: 'DeliverableSettings',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customers'),
          prependIcon: 'mdi-account-group',
          to: {
            name: 'BusinessCustomers',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customer_deliverables') + (this.customerName ? ': ' + this.customerName : ''),
          prependIcon: 'mdi-package-variant',
        },
      ]
    },

    fields() {
      if (this.selectedDeliverable) {
        // Edit mode: deliverable_title + icon + active
        return [
          {
            key: 'deliverable_title',
            name: 'deliverable_title',
            label: this.$t('deliverable_title'),
            type: 'text',
            value: this.selectedDeliverable.deliverable_title,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 12,
          },
          {
            key: 'default_format_desc',
            name: 'default_format_desc',
            label: this.$t('default_format_desc'),
            type: 'text',
            value: this.selectedDeliverable.default_format_desc || '',
            cols: 12,
          },
          {
            key: 'icon',
            name: 'icon',
            label: this.$t('icon'),
            type: 'text',
            value: this.selectedDeliverable.icon || '',
            cols: 12,
          },
          {
            key: 'active',
            name: 'active',
            label: this.$t('active'),
            type: 'switch',
            value: this.selectedDeliverable.active,
            cols: 12,
          },
        ]
      }

      // Create mode: deliverable_title + default_format_desc + icon
      return [
        {
          key: 'deliverable_title',
          name: 'deliverable_title',
          label: this.$t('deliverable_title'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 12,
        },
        {
          key: 'default_format_desc',
          name: 'default_format_desc',
          label: this.$t('default_format_desc'),
          type: 'text',
          value: '',
          cols: 12,
        },
        {
          key: 'icon',
          name: 'icon',
          label: this.$t('icon'),
          type: 'text',
          value: '',
          cols: 12,
        },
      ]
    },
  },

  data() {
    return {
      loading: true,
      deliverables: [],
      options: {
        sortBy: ['deliverable_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_deliverable',
          eventToEmit: 'create-new',
        },
      ],
      selectedDeliverable: null,
      headers: [
        { text: this.$t('deliverable_title'), value: 'deliverable_title', sortable: true },
        { text: this.$t('default_format_desc'), value: 'default_format_desc', sortable: false },
        { text: this.$t('icon'), value: 'icon', sortable: false },
        { text: this.$t('active'), value: 'active', sortable: true },
        { text: this.$t('created_at'), value: 'created_at', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ],
    }
  },

  mounted() {
    this.selectedDeliverable = null
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    openDeliverableBillableModal(deliverable) {
      this.$refs.deliverableBillableModal.openModal('from-deliverable', deliverable)
    },

    newDeliverable() {
      this.selectedDeliverable = null
      this.$refs.modal.openModal()
    },

    editDeliverable(deliverable) {
      this.selectedDeliverable = deliverable
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.modal) {
        console.error('Modal reference not found')
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        formData[field.name] = field.value
      })

      let apiCall

      if (this.selectedDeliverable) {
        const deliverableId = this.selectedDeliverable.deliverable_id

        if (!deliverableId) {
          console.error('Deliverable ID not found in selected deliverable object:', this.selectedDeliverable)
          this.$refs.modal.errorMessage = this.$t('deliverable_id_not_found')
          return
        }

        apiCall = DeliverableAPI.updateDeliverable(deliverableId, {
          deliverable_title: formData.deliverable_title,
          default_format_desc: formData.default_format_desc || null,
          icon: formData.icon || null,
          active: formData.active,
        })
      } else {
        apiCall = DeliverableAPI.createDeliverable({
          customer_id: this.customerId,
          deliverable_title: formData.deliverable_title,
          default_format_desc: formData.default_format_desc || null,
          icon: formData.icon || null,
        })
      }

      apiCall
        .then(() => {
          const message = this.selectedDeliverable
            ? this.$t('deliverable_updated_successfully')
            : this.$t('deliverable_created_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || this.$t('error')
          }
        })
    },

    toggleActive(deliverable, activeValue) {
      const deliverableId = deliverable.deliverable_id

      if (!deliverableId) {
        console.error('Deliverable ID not found:', deliverable)
        return
      }

      DeliverableAPI.updateDeliverable(deliverableId, {
        deliverable_title: deliverable.deliverable_title,
        icon: deliverable.icon || null,
        active: activeValue,
      })
        .then(() => {
          const message = activeValue
            ? this.$t('deliverable_activated_successfully')
            : this.$t('deliverable_deactivated_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error updating deliverable active status:', error)
        })
    },

    reloadData() {
      let options = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      // Remove customerName from query params before sending to API
      delete options.customerName

      this.loading = true
      DeliverableAPI.getAllDeliverablesByCustomer(this.customerId, options)
        .then((result) => {
          this.deliverables = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    getModalTitle() {
      return this.selectedDeliverable ? this.$t('edit_deliverable') : this.$t('new_deliverable')
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },
  },
}
</script>
